// React and Next.js imports
import type { GetStaticProps, GetStaticPaths } from "next";
import { ClinicalTrialWrapper } from "@orlandohealth/build-kit";
import { ClinicalTrialPage } from "@orlandohealth/build-kit";
import { getLocalizedContent } from "@orlandohealth/build-kit";
import { builder } from "@builder.io/react";
import { useEffect } from "react";
import { useRouter } from "next/router";
import { logger } from "@orlandohealth/utils";

// Use ClinicalTrialsPage with DefaultPageWrapper
const Page = ClinicalTrialPage.createPage(
  ClinicalTrialWrapper,
  "clinical-trial"
);

// Conversion tracking for Algolia Insights
const AlgoliaConversionTracker = () => {
  const router = useRouter();
  const isClient = typeof window !== "undefined";
  
  useEffect(() => {
    if (!isClient) return;
    const { queryID, objectID } = router.query;
    if (window.aa && queryID && objectID) {
      window.aa("convertedObjectIDsAfterSearch", {
        index: "idx-clinical-trial",
        eventName: "Clinical Trial Viewed",
        queryID: String(queryID),
        objectIDs: [String(objectID)],
      });
    }
  }, [isClient, router.query]);
  return null;
};

// Export the page component
const ClinicalTrialDetailPage = (props: any) => (
  <>
    <AlgoliaConversionTracker />
    <Page {...props} />
  </>
);

export default ClinicalTrialDetailPage;

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  defaultLocale,
  preview = false,
  previewData = {},
}) => {
  const urlPath = `/clinical-trials/find/${
    (params?.slug as string[])?.join("/") || ""
  }`;
  const currentLocale = locale || defaultLocale;
  const slug = (params?.slug as string[])?.[0];

  const pd = previewData as Record<string, unknown>;
  const isInBuilder =
    Boolean(pd["builder.space"]) || Boolean(pd["builder.frameEditing"]);

  const apikey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";
  // Get base props from SectionPage with cache busting options
  const baseProps = await ClinicalTrialPage.getStaticProps(
    urlPath,
    slug,
    locale,
    defaultLocale,
    "clinical-trial",
    apikey || undefined,
    preview || isInBuilder
  );

  if ("notFound" in baseProps) {
    return baseProps;
  }
  if ("redirect" in baseProps) {
    return baseProps;
  }

  logger.debug("[...slug] baseProps", JSON.stringify(baseProps, null, 2));

  builder.init(apikey);

  //Get related trials with cache control
  const relatedTrials = await builder
    .getAll("clinical-trial", {
      fields: "data.title,data.slug,data.date",
      options: {
        noTargeting: true,
        cachebust: true, // Add cache busting
      },
      limit: 4,
      locale: currentLocale,
      query: {
        "data.slug": {
          $ne: slug,
        },
      },
    })
    .then((trials) =>
      trials.map((trial) => {
        return {
          title:
            getLocalizedContent(trial.data?.title, currentLocale || "en") || "",
          url: `/clinical-trials/find/${trial.data?.slug}`,
          date: trial.data?.date || null,
        };
      })
    )
    .catch((error) => {
      console.error("Error fetching related trials:", error);
      return [];
    });

  // Merge the props and reduce revalidate time
  return {
    props: {
      ...baseProps.props,
      relatedTrials,
    },
    revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales = ["en"] }) => {
  if (process.env.SKIP_SSG === "true") {
    return { paths: [], fallback: true };
  }
  const paths = [];

  // Handle each locale
  const apikey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";
  builder.init(apikey);
  for (const locale of locales) {
    // Query Builder.io for all clinical trial models
    const trials = await builder.getAll("clinical-trial", {
      fields: "data.slug",
      options: { noTargeting: true },
      locale: locale,
    });

    // Add paths for each trial in each locale
    paths.push(
      ...trials.map((trial) => ({
        params: {
          slug: [trial.data?.slug],
        },
        locale,
      }))
    );
  }

  return {
    paths,
    fallback: true,
  };
};
